import React from 'react';
import { useTheme } from '@material-ui/core/styles';
import { alpha } from '@mui/material/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Grid from '@material-ui/core/Grid';
import Campaign from '@mui/icons-material/Campaign';
import Explore from '@mui/icons-material/Explore';
import RocketLaunch from '@mui/icons-material/RocketLaunch';
import MenuBook from '@mui/icons-material/MenuBook';
import Star from '@mui/icons-material/Star';
import TrendingUp from '@mui/icons-material/TrendingUp';
import EmojiEvents from '@mui/icons-material/EmojiEvents';
import BusinessCenter from '@mui/icons-material/BusinessCenter';
import Groups from '@mui/icons-material/Groups';
import MovieCreation from '@mui/icons-material/MovieCreation';
import { EN_PRODUCCION_MEM_EJES_LISTA, EN_PRODUCCION_MEM_PARRAFO_PROGRAMA } from './enProduccionConMemHeroCopy';
import { EN_PRODUCCION_CON_MEM_LOGO_SRC, EN_PRODUCCION_CON_MEM_TNO_SHOW_URL } from '../brandAssets';

const iconSx = { fontSize: 28, color: 'inherit' };

const LineRow = ({ icon, children, sx = {} }) => (
  <Box display="flex" alignItems="flex-start" gap={1.75} sx={{ mb: 2, ...sx }}>
    <Box
      sx={{
        color: (t) => t.palette.secondary.main,
        display: 'flex',
        flexShrink: 0,
        mt: 0.35,
        opacity: 0.95,
      }}
    >
      {React.cloneElement(icon, { sx: iconSx })}
    </Box>
    <Box flex={1} sx={{ minWidth: 0 }}>
      {children}
    </Box>
  </Box>
);

const ejesIconos = [
  <TrendingUp key="t" />,
  <EmojiEvents key="e" />,
  <BusinessCenter key="b" />,
  <Groups key="g" />,
];

/**
 * Hero en dos columnas: panel con gradiente suave + logo (izq.) · texto con iconos (der.).
 */
const HeroMediaKitCover = () => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'), { defaultMatches: true });
  const accent = theme.palette.secondary.main;
  const deep = theme.palette.primary.main;
  const deepDark = theme.palette.primary.dark;
  const paperBg = theme.palette.alternate?.main || '#f7f3fc';

  return (
    <Box
      sx={{
        borderRadius: isSm ? 4 : 0,
        boxShadow: 3,
        overflow: 'hidden',
        border: `1px solid ${alpha(deep, 0.2)}`,
      }}
    >
      <Grid container alignItems="stretch">
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            display: 'flex',
            borderRight: { md: `2px solid ${alpha(deep, 0.26)}` },
            borderBottom: { xs: `2px solid ${alpha(deep, 0.2)}`, md: 'none' },
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: { xs: 220, sm: 260, md: 'auto' },
              py: { xs: 4, md: 5 },
              px: 3,
              position: 'relative',
              background: `
                radial-gradient(ellipse 100% 80% at 50% 20%, ${alpha(accent, 0.28)} 0%, transparent 55%),
                linear-gradient(168deg, ${deepDark} 0%, ${deep} 45%, ${theme.palette.primary.light} 100%)
              `,
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 240,
                borderRadius: 2,
                border: `2px solid ${accent}`,
                p: 2.5,
                bgcolor: '#fff',
                boxShadow: `0 8px 32px ${alpha('#000', 0.1)}`,
              }}
            >
              <Box
                component="img"
                src={EN_PRODUCCION_CON_MEM_LOGO_SRC}
                alt="En Producción con MEM — logo oficial"
                sx={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  filter: `drop-shadow(0 1px 4px ${alpha('#000', 0.12)})`,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 1.5,
                  color: 'text.secondary',
                  lineHeight: 1.5,
                  fontWeight: 500,
                  textAlign: 'center',
                }}
              >
                con María Eugenia Moreno
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Box
            sx={{
              height: '100%',
              px: { xs: 2.25, sm: 3, md: 3.5 },
              py: { xs: 2.5, sm: 3.5, md: 4 },
              bgcolor: paperBg,
              color: 'text.primary',
            }}
          >
            <LineRow icon={<Campaign />}>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontWeight: 700,
                  color: accent,
                  lineHeight: 1.3,
                }}
              >
                Creatividad con respaldo.
              </Typography>
            </LineRow>

            <LineRow icon={<Explore />}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontWeight: 700,
                  color: accent,
                  lineHeight: 1.35,
                }}
              >
                Publicidad, marcas y negocio en pantalla
              </Typography>
            </LineRow>

            <LineRow icon={<RocketLaunch />}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontStyle: 'italic',
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontWeight: 600,
                  color: accent,
                  lineHeight: 1.45,
                }}
              >
                Donde la pauta encuentra su historia.
              </Typography>
            </LineRow>

            <LineRow icon={<MenuBook />}>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                {EN_PRODUCCION_MEM_PARRAFO_PROGRAMA}
              </Typography>
            </LineRow>

            <LineRow icon={<Star />} sx={{ mb: 1.25 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: accent,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontSize: '0.95rem',
                }}
              >
                Cuatro ejes de conversación con la audiencia
              </Typography>
            </LineRow>

            {EN_PRODUCCION_MEM_EJES_LISTA.map((linea, i) => (
              <LineRow key={linea} icon={ejesIconos[i]} sx={{ mb: 1.35 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
                  {linea}
                </Typography>
              </LineRow>
            ))}

            <LineRow icon={<MovieCreation />} sx={{ mb: 1.5, mt: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                Presentado por María Eugenia Moreno · Streaming en vivo y On-Demand en TNO Network. Horario y avisos:{' '}
                <Link
                  href="https://www.instagram.com/tnonetwork/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: accent, fontWeight: 600 }}
                  underline="hover"
                >
                  @tnonetwork
                </Link>
              </Typography>
            </LineRow>

            <Box sx={{ pl: { xs: 0, sm: 5 }, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link
                href={EN_PRODUCCION_CON_MEM_TNO_SHOW_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: accent, fontWeight: 600, fontSize: '0.95rem', width: 'fit-content' }}
                underline="hover"
              >
                Ver En Producción con MEM en TNO
              </Link>
              <Link
                href="https://www.tnonetwork.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.95rem', width: 'fit-content' }}
                underline="hover"
              >
                tnonetwork.com
              </Link>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HeroMediaKitCover;
